import cx from 'classnames';
import {
  ControlNamespace,
  ControlProps,
  FormV2,
  ITextareaTextProps,
  TextareaProps,
} from 'components/ui';
import dynamic from 'next/dynamic';
import { forwardRef } from 'react';
import { Form as BootstrapForm } from 'react-bootstrap';
import { AsyncSelect } from './AsyncSelect';
import { UtcDatePicker } from './DatePicker';
import css from './Form.module.scss';
import { HourPicker } from './HourPicker';
import { TimeInput } from './TimeInput';

// Select components are imported dynamically due to a bug in react-select that causes SSR issues.
// https://github.com/JedWatson/react-select/issues/5459

const CheckboxMultiSelect = dynamic(
  async () => (await import('./Select')).CheckboxMultiSelect,
  { ssr: false }
);

const Select = dynamic(async () => (await import('./Select')).Select, {
  ssr: false,
});

const Control = forwardRef<typeof FormV2.Control, ControlProps>(
  ({ className, ...rest }, ref) => (
    <FormV2.Control
      className={cx(css.control, className)}
      ref={ref}
      {...rest}
    />
  )
);

Control.displayName = 'Control';
(Control as ControlNamespace).Feedback = FormV2.Control.Feedback;

const Textarea = ({ className, ...rest }: TextareaProps) => (
  <FormV2.Textarea className={cx(css.textarea, className)} {...rest} />
);

const TextareaText = (props: ITextareaTextProps) => (
  <FormV2.TextareaText linkClassName={css.link} {...props} />
);

export const Form = {
  ...BootstrapForm,
  AsyncSelect,
  AsyncSelectV2: FormV2.AsyncSelect,
  Checkbox: FormV2.Checkbox,
  CheckboxMultiSelect,
  Control,
  Details: FormV2.Details,
  Group: FormV2.Group,
  HourPicker,
  Label: FormV2.Label,
  Radio: FormV2.Radio,
  RichText: FormV2.RichText,
  S3SingleFileUpload: FormV2.S3SingleFileUpload,
  Select,
  Textarea,
  SingleFileUpload: FormV2.SingleFileUpload,
  TextareaText,
  TimeInput,
  Toggle: FormV2.Toggle,
  UtcDatePicker,
} as typeof BootstrapForm & {
  AsyncSelect: any; // TODO: fix typing
  AsyncSelectV2: typeof FormV2.AsyncSelect;
  Checkbox: typeof FormV2.Checkbox;
  CheckboxMultiSelect: any; // TODO: fix typing
  Control: typeof FormV2.Control;
  Details: typeof FormV2.Details;
  Group: typeof FormV2.Group;
  HourPicker: typeof HourPicker;
  Label: typeof FormV2.Label;
  Radio: typeof FormV2.Radio;
  RichText: typeof FormV2.RichText;
  S3SingleFileUpload: typeof FormV2.S3SingleFileUpload;
  Select: any; // TODO: fix typing
  SingleFileUpload: any; // TODO: fix typing
  Textarea: typeof FormV2.Textarea;
  TextareaText: typeof TextareaText;
  TimeInput: typeof TimeInput;
  Toggle: typeof FormV2.Toggle;
  UtcDatePicker: any; // TODO: fix typing
};
